// Gestes et temps réel de la tour, partagés par la page, le détail, la
// carte mobile et le panneau d'un retour.
import { useEffect, useRef, useState } from 'preact/hooks'
import { isTowerEvent, type FeedbaacksClient, type TowerTicketDetail, type TowerTicketRow } from '@feedbaacks/sdk'
import { errorMessage, useFeedbaacks } from '@feedbaacks/ui'

/** Un geste : occupé pendant l'appel, notification, puis rechargement. */
export function useTowerAct(onChanged: () => void) {
  const { client, notify, confirm } = useFeedbaacks()
  const [busy, setBusy] = useState(false)
  const act = async (f: (c: FeedbaacksClient) => Promise<unknown>, ok: string, fail: string): Promise<boolean> => {
    setBusy(true)
    try {
      await f(client)
      notify('success', ok)
      return true
    } catch (err) {
      notify('error', `${fail} : ${errorMessage(err, 'erreur inattendue')}`)
      return false
    } finally {
      setBusy(false)
      onChanged()
    }
  }
  return {
    busy,
    act,
    /** Approuve sur le SHA affiché, puis ouvre la PR. */
    approve: (t: TowerTicketDetail, open: (url: string) => void) =>
      act(async (c) => {
        const r = await c.tower.approve(t.id, t.head_sha ?? '')
        if (r.pr_url) open(r.pr_url)
      }, 'Approuvé : la PR est ouverte sur Bitbucket.', 'Approbation refusée'),
    sendBack: (id: string, note: string) => act((c) => c.tower.sendBack(id, note), 'Renvoyé à l\'agent.', 'Renvoi impossible'),
    /** fresh : « Repartir de zéro » ; sinon l'agent reprend sa branche avec les remarques. */
    retry: (id: string, fresh = false) => act((c) => c.tower.retry(id, fresh),
      fresh ? 'Relancé de zéro sur la branche à jour.' : 'Relancé : l\'agent reprend sa branche.', 'Relance impossible'),
    /** Relancer un ticket livré : nouveau cycle complet, jusqu'à une nouvelle PR. */
    relaunchDelivered: async (t: Pick<TowerTicketDetail, 'id' | 'code' | 'default_branch'>) => {
      const yes = await confirm({
        title: `Relancer ${t.code} ?`,
        message: `L'agent repart de ${t.default_branch}, qui contient la livraison, avec les remarques de l'équipe. CI, revue, preview et approbation suivent, puis une nouvelle PR. Une enveloppe de budget neuve est ouverte.`,
        confirmLabel: 'Relancer',
      })
      return yes ? act((c) => c.tower.retry(t.id), `${t.code} relancé après livraison.`, 'Relance impossible') : false
    },
    /** Arrêter coûte le travail en cours : toujours confirmé. */
    stop: async (t: Pick<TowerTicketRow, 'id' | 'code'>) => {
      const yes = await confirm({
        title: `Arrêter ${t.code} ?`, message: 'Les Jobs en cours s\'arrêtent au prochain battement. Le ticket pourra être relancé.',
        confirmLabel: 'Arrêter', destructive: true,
      })
      return yes ? act((c) => c.tower.stop(t.id), `${t.code} arrêté.`, 'Arrêt impossible') : false
    },
    archive: (t: Pick<TowerTicketRow, 'id' | 'code'>, archived: boolean) => archived
      ? act((c) => c.tower.archive(t.id), `${t.code} archivé.`, 'Archivage impossible')
      : act((c) => c.tower.unarchive(t.id), `${t.code} sorti des archives.`, 'Action impossible'),
    archiveMany: async (rows: TowerTicketRow[]) => {
      const n = rows.length
      const yes = await confirm({
        title: `Archiver ${n} ticket${n > 1 ? 's' : ''} ?`,
        message: 'Ils quittent la liste des demandes. Rien n\'est effacé : le filtre « Archivés » les garde, et « Relancer » en ressort un.',
        confirmLabel: 'Archiver',
      })
      return yes ? act((c) => c.tower.archiveMany(rows.map((t) => t.id)), `${n} ticket${n > 1 ? 's' : ''} archivé${n > 1 ? 's' : ''}.`, 'Archivage impossible') : false
    },
  }
}

/** Temps réel de la tour : `onChange` à chaque salve d'événements tower.*
 *  (regroupés, un geste groupé n'en déclenche qu'un), `live` tant que le
 *  flux tient ; le sondage de repli ne tourne que flux coupé. */
export function useTowerRealtime(onChange: () => void, fallbackMs = 15_000, debounceMs = 300): { live: boolean } {
  const { client } = useFeedbaacks()
  const [live, setLive] = useState(true)
  const cb = useRef(onChange)
  useEffect(() => {
    cb.current = onChange
  }, [onChange])
  useEffect(() => {
    let timer: number | undefined
    const soon = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => cb.current(), debounceMs)
    }
    const off = client.subscribe((e) => {
      if (isTowerEvent(e)) soon()
    }, () => cb.current(), setLive)
    return () => {
      window.clearTimeout(timer)
      off()
    }
  }, [client, debounceMs])
  useEffect(() => {
    if (live) return
    const t = window.setInterval(() => cb.current(), fallbackMs)
    return () => window.clearInterval(t)
  }, [live, fallbackMs])
  return { live }
}

/** Raccourcis clavier hors des champs de saisie. */
export function useHotkeys(handler: (e: KeyboardEvent) => void, enabled = true): void {
  const cb = useRef(handler)
  useEffect(() => {
    cb.current = handler
  }, [handler])
  useEffect(() => {
    if (!enabled) return
    const on = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const el = e.target as HTMLElement | null
      if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) {
        if (e.key === 'Escape') el.blur()
        return
      }
      if (document.querySelector('.fb-confirm-backdrop')) return
      cb.current(e)
    }
    window.addEventListener('keydown', on)
    return () => window.removeEventListener('keydown', on)
  }, [enabled])
}
